import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import { X } from 'lucide-react';
import { FAMILY_GAMES } from '../data/familyGames';

export default function GamePickerModal({ isOpen, onClose, onSelectGame }) {
  return (
    <AnimatePresence>
      {isOpen && (
        <>
          <Overlay
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
          />
          <Sheet
            initial={{ y: '100%' }}
            animate={{ y: 0 }}
            exit={{ y: '100%' }}
            transition={{ type: 'spring', damping: 28, stiffness: 300 }}
          >
            <SheetHandle />
            <CloseBtn onClick={onClose}><X size={20} /></CloseBtn>

            <Title>Family Games</Title>
            <Subtitle>Pick a game to play with your circle</Subtitle>

            <GameList>
              {FAMILY_GAMES.map((game) => (
                <GameRow key={game.id} onClick={() => onSelectGame(game)}>
                  <GameEmoji>{game.emoji}</GameEmoji>
                  <GameInfo>
                    <GameName>{game.name}</GameName>
                    <GameDesc>{game.shortDesc}</GameDesc>
                  </GameInfo>
                </GameRow>
              ))}
            </GameList>
          </Sheet>
        </>
      )}
    </AnimatePresence>
  );
}

const Overlay = styled(motion.div)`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.4);
  z-index: 200;
`;

const Sheet = styled(motion.div)`
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  max-height: 75vh;
  overflow-y: auto;
  background: ${({ theme }) => theme.colors.background};
  border-radius: 20px 20px 0 0;
  padding: 16px 24px 32px;
  z-index: 201;
  padding-bottom: max(32px, env(safe-area-inset-bottom));
`;

const SheetHandle = styled.div`
  width: 40px;
  height: 4px;
  border-radius: 2px;
  background: ${({ theme }) => theme.colors.border};
  margin: 0 auto 16px;
`;

const CloseBtn = styled.button`
  position: absolute;
  top: 16px;
  right: 16px;
  width: 36px;
  height: 36px;
  padding: 0;
  border: none;
  background: none;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${({ theme }) => theme.colors.textSecondary};
  cursor: pointer;

  &:hover {
    background: ${({ theme }) => theme.colors.surface};
  }
`;

const Title = styled.h2`
  font-size: 22px;
  font-weight: 800;
  text-align: center;
  margin-bottom: 4px;
`;

const Subtitle = styled.p`
  font-size: 14px;
  color: ${({ theme }) => theme.colors.textSecondary};
  text-align: center;
  margin-bottom: 20px;
`;

const GameList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const GameRow = styled.button`
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 14px 16px;
  border-radius: 14px;
  background: ${({ theme }) => theme.colors.surfaceSecondary};
  border: 1px solid ${({ theme }) => theme.colors.border};
  text-align: left;
  width: 100%;
  transition: all 0.2s;

  &:hover {
    border-color: ${({ theme }) => theme.colors.accent};
    background: ${({ theme }) => theme.colors.surface};
    transform: translateY(-1px);
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  }

  &:active {
    transform: scale(0.98);
  }
`;

const GameEmoji = styled.span`
  font-size: 28px;
  flex-shrink: 0;
`;

const GameInfo = styled.div`
  flex: 1;
  min-width: 0;
`;

const GameName = styled.div`
  font-size: 15px;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.text};
  margin-bottom: 2px;
`;

const GameDesc = styled.div`
  font-size: 13px;
  color: ${({ theme }) => theme.colors.textSecondary};
`;
